import React, { useEffect, useState, useCallback } from 'react';
import { Box, CardActions, IconButton, Modal, CardHeader } from '@mui/material';
import axios from 'axios';
import { pdfjs, Document, Page } from 'react-pdf';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
import authorizationHeader from '../../../shared/services/authorizationHeader';
import { ANMELDUNG_ABGELAUFEN_HINWEIS, istAnmeldungAbgelaufen } from '../../../shared/services/anmeldungAbgelaufen';
import { useSnackbar } from '../../snackbar';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

// Jede Öffnung und jeder Dateiwechsel bekommt einen eigenen Dokumentzustand. So kann auch
// vor dem nächsten Effekt kein altes PDF unter dem neuen Dateinamen gerendert werden.
function PdfPreviewContent({
  handleClose,
  pdfPath,
  filename,
  zoom,
  setZoom,
}: {
  handleClose: () => void;
  pdfPath: string;
  filename?: string | null;
  zoom: number;
  setZoom: React.Dispatch<React.SetStateAction<number>>;
}) {
  const [pdfFile, setPdfFile] = useState<Blob | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    let aktiv = true;
    const fetchPdf = async () => {
      try {
        const response = await axios.get<Blob>(pdfPath, {
          responseType: 'blob',
          headers: authorizationHeader(),
        });
        if (aktiv) setPdfFile(response.data);
      } catch (error) {
        // Auch ein verspäteter Fehler darf die inzwischen geöffnete Datei nicht schließen.
        if (!aktiv) return;
        console.error('Error fetching pdf', error);
        if (istAnmeldungAbgelaufen((error as { response?: { status?: number } })?.response?.status)) {
          enqueueSnackbar(ANMELDUNG_ABGELAUFEN_HINWEIS, { variant: 'error' });
        }
        handleClose();
      }
    };
    fetchPdf();
    return () => {
      aktiv = false;
    };
  }, [pdfPath]);

  const onDocumentLoadSuccess = ({ numPages: nextNumPages }: { numPages: number }) => {
    setNumPages(nextNumPages);
  };

  const handlePreviousPage = useCallback(() => {
    setPageNumber(pageNumber - 1);
  }, [pageNumber]);

  const handleNextPage = useCallback(() => {
    setPageNumber(pageNumber + 1);
  }, [pageNumber]);

  const handleZoomIn = useCallback(() => {
    setZoom(zoom + 0.1);
  }, [zoom]);

  const handleZoomOut = useCallback(() => {
    setZoom(zoom - 0.1);
  }, [zoom]);

  return (
    <>
      {pdfFile === null && <div>Loading...</div>}
      {pdfFile && (
        <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <CardHeader
            title="Vorschau"
            action={
              <IconButton aria-label="settings" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            }
            subheader={filename ?? ''}
          />
          <CardContent sx={{ flex: 1, overflow: 'auto' }}>
            <Box display="flex" sx={{ justifyContent: 'center', minHeight: '200px' }}>
              <Document file={pdfFile} onLoadSuccess={onDocumentLoadSuccess}>
                <Page pageNumber={pageNumber} renderTextLayer={false} renderAnnotationLayer={false} scale={zoom} />
              </Document>
            </Box>
          </CardContent>
          <CardActions>
            <Box display="flex" justifyContent="center" width="100%" alignItems="center">
              <IconButton onClick={handlePreviousPage} disabled={pageNumber === 1}>
                <ArrowBackIosIcon />
              </IconButton>
              <Box display="flex" justifyContent="center" alignItems="center" sx={{ mx: 2 }}>
                Seite {pageNumber} von {numPages}
              </Box>
              <IconButton onClick={handleNextPage} disabled={pageNumber >= numPages}>
                <ArrowForwardIosIcon />
              </IconButton>

              <IconButton onClick={handleZoomIn} sx={{ ml: 2 }}>
                <ZoomInIcon />
              </IconButton>
              <IconButton onClick={handleZoomOut}>
                <ZoomOutIcon />
              </IconButton>
            </Box>
          </CardActions>
        </Card>
      )}
    </>
  );
}

function PdfPreview({
  open,
  handleClose,
  pdfPath,
  filename,
}: {
  open: boolean;
  handleClose: () => void;
  pdfPath: string | null;
  filename?: string | null;
}) {
  const [zoom, setZoom] = useState(1);
  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') handleClose();
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80vw',
          maxHeight: '90vh',
          outline: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {open && pdfPath && (
          <PdfPreviewContent
            key={pdfPath}
            pdfPath={pdfPath}
            filename={filename}
            handleClose={handleClose}
            zoom={zoom}
            setZoom={setZoom}
          />
        )}
      </Box>
    </Modal>
  );
}

export default PdfPreview;
